import {useMemo} from 'react';
import {Outlet, useParams} from 'react-router-dom';

import {useGetUser, useGetUserCurrency} from '@entities/user';
import {CardBox, ClassicMuiNavTabs} from '@shared/components';
import {
  ROUTE__USER_ACCOUNT__BANS,
  ROUTE__USER_ACCOUNT__DEPOSITS,
  ROUTE__USER_ACCOUNT__INFORMATION,
  ROUTE__USER_ACCOUNT__ORDERS,
  ROUTE__USER_ACCOUNT__POOLS,
  ROUTE__USER_ACCOUNT__STAKES,
  ROUTE__USER_ACCOUNT__TRANSACTIONS,
  ROUTE__USER_ACCOUNT__TRANSACTIONS_POOL,
  ROUTE__USER_ACCOUNT__UTM,
  ROUTE__USER_ACCOUNT__WITHDRAWALS,
} from '@shared/constants';
import {BaseWidgetLayout, Header} from '@widgets/common';
import {UserAccountOutletContext} from './types';

import {NavTabAttrs} from '@/shared/types';
import {COLOR__WHITE} from '@/theme/colors';

// `route` still contains the `:id` placeholder, it is resolved per user below
const tabsConfig: NavTabAttrs[] = [
  {route: ROUTE__USER_ACCOUNT__INFORMATION, label: 'Information'},
  {route: ROUTE__USER_ACCOUNT__BANS, label: 'Bans'},
  {route: ROUTE__USER_ACCOUNT__UTM, label: 'UTM'},
  {route: ROUTE__USER_ACCOUNT__ORDERS, label: 'Orders'},
  {route: ROUTE__USER_ACCOUNT__STAKES, label: 'Stakes'},
  {route: ROUTE__USER_ACCOUNT__POOLS, label: 'Pools'},
  {route: ROUTE__USER_ACCOUNT__TRANSACTIONS, label: 'Transactions'},
  {
    route: ROUTE__USER_ACCOUNT__TRANSACTIONS_POOL,
    label: 'Transactions (pools)',
  },
  {route: ROUTE__USER_ACCOUNT__DEPOSITS, label: 'Deposits'},
  {route: ROUTE__USER_ACCOUNT__WITHDRAWALS, label: 'Withdrawals'},
];

export const UserAccountPage = () => {
  const {id: userId} = useParams<{id: string}>();

  const {
    data: user,
    isPending,
    error,
  } = useGetUser(userId ?? '', {enabled: !!userId});
  const {data: currency} = useGetUserCurrency(user?.username ?? '', {
    enabled: !!user?.username,
  });

  const tabs = useMemo<NavTabAttrs[]>(
    () =>
      tabsConfig.map(({route, label}) => ({
        route: route.replace(':id', userId ?? ''),
        label,
      })),
    [userId],
  );

  return (
    <>
      <Header title={user ? `@${user.username}` : `User ${userId}`} mb={2} />

      <BaseWidgetLayout isPending={isPending} error={error ?? null}>
        {user && (
          <CardBox bgcolor={COLOR__WHITE}>
            <ClassicMuiNavTabs tabs={tabs} sx={{mb: 3}} />

            <Outlet
              context={
                {
                  user,
                  currency: currency ?? null,
                } satisfies UserAccountOutletContext
              }
            />
          </CardBox>
        )}
      </BaseWidgetLayout>
    </>
  );
};
